import { fetcher } from 'utils/axios';
import { ChatUserModelOriginal } from 'types/chat-users/chat-user';
import {
  MassMessagingGroupDetailsItem,
  MassMessagingGroupMemberData,
  MassMessagingGroupType,
} from 'types/mass-messaging/groups';

export const fetchGetMassMessagingGroups = async () => {
  try {
    const data = await fetcher('get', '/api/massMessaging/getCompanyMassMessagingGroups', 'history');
    if (data)
      return data as {
        companyMassMessagingGroups: MassMessagingGroupType[];
        groupDetailsList: MassMessagingGroupDetailsItem[];
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in getMassMessagingGroups:', error);
    return null;
  }
};
export const fetchGetMassMessagingGroupMembers = async (groupId: number) => {
  try {
    const data = await fetcher(
      'get',
      `/api/massMessaging/getFunnerMassMessagingGroupMembers/${groupId}`,
      'history',
    );

    if (data)
      return data as {
        result: boolean;
        readyMembers: Array<{
          chatUser: ChatUserModelOriginal | null;
          memberData: MassMessagingGroupMemberData;
        }>;
      };
    else return null;
  } catch (error) {
    console.warn('Error in getMassMessagingGroupMembers:', error);
    return null;
  }
};
export const fetchCreateMassMessagingGroup = async (groupData: {
  Name: string;
  Description: string | null;
}) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/createFunnerMassMessagingGroup',
        {
          data: groupData,
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
        groupId: number;
      };
    else return null;
  } catch (error) {
    console.warn('Error in createMassMessagingGroup:', error);
    return null;
  }
};
export const fetchUpdateMassMessagingGroup = async (
  id: number,
  groupData: {
    Name: string;
    Description: string | null;
  },
) => {
  try {
    const data = await fetcher(
      'post',
      [
        `/api/massMessaging/updateFunnerMassMessagingGroup/${id}`,
        {
          data: groupData,
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in updateMassMessagingGroup:', error);
    return null;
  }
};
export const fetchDeleteMassMessagingGroup = async (groupId: number) => {
  try {
    const data = await fetcher(
      'post',
      `/api/massMessaging/deleteFunnerMassMessagingGroup/${groupId}`,
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in deleteMassMessagingGroup:', error);
    return null;
  }
};
export const fetchDuplicateMassMessagingGroup = async (groupId: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/duplicateFunnerMassMessagingGroup',
        {
          data: { id: groupId },
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in deleteMassMessagingGroup:', error);
    return null;
  }
};
export const fetchAddFunnerMassMessagingGroupMember = async (params: {
  userPhoneNumberIds: string[];
  MassMessagingGroupId: number;
}): Promise<{ result: boolean; quotaError?: unknown } | null> => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/addFunnerMassMessagingGroupMember',
        {
          data: params,
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    // חסימת תקרת גודל רשימה (422 QUOTA_EXCEEDED, אכיפת מגבלות שלב ב') — מוחזרת
    // לקורא כדי שהמסך יציג הודעה מתורגמת (resolveQuotaErrorMessage).
    const body = (error as { response?: { data?: { error?: string } } })?.response?.data
      ?? (error as { error?: string });
    if (body && typeof body === 'object' && body.error === 'QUOTA_EXCEEDED') {
      return { result: false, quotaError: body };
    }
    console.warn('Error in addFunnerMassMessagingGroupMember:', error);
    return null;
  }
};
export const fetchAppFunnerMassMessagingGroupMemberFileList = async (params: {
  users: {
    [key: string]: any;
  }[];
  MassMessagingGroupId: number;
}) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/addFunnerMassMessagingGroupMemberFileList',
        {
          data: params,
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in addFunnerMassMessagingGroupMemberFileList:', error);
    return null;
  }
};
export const fetchDeleteMassMessagingGroupMember = async (id: number) => {
  try {
    const data = await fetcher(
      'post',
      `/api/massMessaging/deleteFunnerMassMessagingGroupMember/${id}`,
      'history',
    );

    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in deleteFunnerMassMessagingGroupMember:', error);
    return null;
  }
};
export const fetchDeleteMassMessagingGroupMembersList = async (idlistToRemove: number[]) => {
  if (idlistToRemove.length === 0) {
    return { result: true };
  }
  try {
    const data = await fetcher(
      'post',
      [
        '/api/massMessaging/deleteFunnerMassMessagingGroupMemberList',
        {
          data: { idlistToRemove },
        },
      ],
      'history',
    );

    if (data && (data as { result: boolean }).result) {
      return data as { result: boolean };
    }

    // תגובה מפורשת מהשרת שהמחיקה המרובתית נכשלה — לא מנסים שוב אחד־אחד (למנוע כפילויות)
    if (data && !(data as { result: boolean }).result) {
      return data as { result: boolean };
    }

    // null/undefined — לרוב 404 או כשל רשת: נקודת הקצה למחיקה מרובתית לא קיימת בסביבה — מחיקה בודדת
    const results = await Promise.all(
      idlistToRemove.map((memberId) => fetchDeleteMassMessagingGroupMember(memberId)),
    );
    const allOk = results.every((r) => r?.result);
    return { result: allOk };
  } catch (error) {
    console.warn('Error in deleteMassMessagingGroupMembersList:', error);
    return null;
  }
};
